import React from "react";
import { X, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-parchment rounded-md px-3 py-2.5 text-ink bg-white focus:outline-none focus:border-forest";

const EMPTY = {
  title: "", description: "", media_type: "image", media_url: "", poster: "",
  link_url: "", link_label: "Learn More", featured: false, published: true,
};

const IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "gif"];
const VIDEO_EXT = ["mp4", "webm", "mov", "ogg"];

export default function AdvertEditorModal({ advert, onClose, onSaved }) {
  const isNew = advert === "new";
  const [form, setForm] = React.useState(isNew ? EMPTY : { ...EMPTY, ...advert });
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState("");
  const [uploadPct, setUploadPct] = React.useState(null);

  const uploadFile = async (e, field) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isVideo = VIDEO_EXT.includes(ext);
    if (field === "media_url" ? ![...IMAGE_EXT, ...VIDEO_EXT].includes(ext) : !IMAGE_EXT.includes(ext)) {
      toast.error(field === "media_url" ? "Upload an image or a video file." : "Poster must be an image.");
      return;
    }
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      setUploadPct(0);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (field === "media_url") {
        setForm((f) => ({ ...f, media_url: url, media_type: isVideo ? "video" : "image" }));
      } else {
        setForm((f) => ({ ...f, poster: url }));
      }
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(""); setUploadPct(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.media_url.trim()) { toast.error("Please upload an image or video for the advert."); return; }
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, media_type: form.media_type,
      media_url: form.media_url, poster: form.poster, link_url: form.link_url.trim(),
      link_label: form.link_label.trim() || "Learn More", featured: form.featured, published: form.published,
    };
    try {
      if (isNew) { await api.post("/admin/adverts", payload); toast.success("Advert posted."); }
      else { await api.put(`/admin/adverts/${advert.id}`, payload); toast.success("Advert updated."); }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="advert-editor">
      <div className="absolute inset-0 bg-night/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-serif text-2xl text-ink">{isNew ? "New Advert" : "Edit Advert"}</h3>
          <button onClick={onClose} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Advert image or video <span className="text-ink-muted font-normal">(images up to 10MB, videos up to 200MB)</span></label>
            <label className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading === "media_url" ? "Uploading…" : "Upload media"}
              <input type="file" accept="image/*,video/*" onChange={(e) => uploadFile(e, "media_url")} disabled={uploading !== ""} data-testid="advert-media-upload-input" className="hidden" />
            </label>
            <input value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} placeholder="…or paste an image/video URL" data-testid="advert-media-url-input" className={`${inputCls} mt-2`} />
            {uploadPct !== null && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-ink-muted mb-1"><span>Uploading…</span><span>{uploadPct}%</span></div>
                <div className="h-2 rounded-full bg-parchment overflow-hidden"><div className="h-full bg-gold transition-all duration-200" style={{ width: `${uploadPct}%` }} /></div>
              </div>
            )}
            {form.media_url && (
              form.media_type === "video"
                ? <video src={form.media_url} controls playsInline preload="metadata" poster={form.poster || undefined} className="w-full h-40 object-contain bg-black rounded-lg border border-parchment mt-3" />
                : <img src={form.media_url} alt="advert" className="w-full h-40 object-cover rounded-lg border border-parchment mt-3" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Media type</label>
              <select value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })} data-testid="advert-media-type-select" className={inputCls}>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>
            {form.media_type === "video" && (
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Poster (optional)</label>
                <label className="inline-flex items-center gap-2 border border-parchment text-ink text-sm font-semibold px-3 py-2 rounded-full hover:border-forest cursor-pointer">
                  {uploading === "poster" ? "Uploading…" : "Upload poster"}
                  <input type="file" accept="image/*" onChange={(e) => uploadFile(e, "poster")} disabled={uploading !== ""} className="hidden" />
                </label>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Title <span className="text-ink-muted font-normal">(optional)</span></label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="advert-title-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Description <span className="text-ink-muted font-normal">(optional)</span></label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="advert-description-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Link URL <span className="text-ink-muted font-normal">(optional)</span></label>
              <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https:// or https://wa.me/…" data-testid="advert-link-url-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Button label</label>
              <input value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} placeholder="Learn More" data-testid="advert-link-label-input" className={inputCls} />
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm text-ink bg-sand border border-parchment rounded-lg px-3 py-2.5">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="advert-featured-checkbox" className="w-4 h-4 accent-gold mt-0.5" />
            <span>Feature on homepage <span className="block text-xs text-ink-muted">Shows this advert as a large banner on the home page. Only one advert can be featured at a time.</span></span>
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="advert-published-checkbox" className="w-4 h-4 accent-forest" />
            Published (visible to visitors)
          </label>
          <button type="submit" disabled={saving || uploading !== ""} data-testid="save-advert-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
            {saving ? "Saving..." : "Save Advert"}
          </button>
        </form>
      </div>
    </div>
  );
}
